import React from 'react';
import * as d3 from 'd3';
import * as styles from '../../styles/components/histogram.css';
import { GroupData, OriginalDataItem } from '../../../types/histogram.types';

interface HistogramFacetedProps {
  groupedData: GroupData[];
  bins: number;
  width: number;
  height: number;
  orientation: 'vertical' | 'horizontal';
  selectedIndices: number[];
  onBinClick?: (minValue: number, maxValue: number, dataIndices: number[], isAdditive?: boolean) => void;
  showDensity?: boolean;
  densityBandwidth?: number;
  showAnnotations?: {
    mean?: boolean;
    median?: boolean;
  };
  // Histogram コンポーネント（循環参照回避のためpropsで渡す）
  HistogramComponent: React.ComponentType<{
    data: number[];
    originalData: OriginalDataItem[];
    width: number;
    height: number;
    bins: number;
    onBinClick?: (minValue: number, maxValue: number, dataIndices: number[], isAdditive?: boolean) => void;
    selectedIndices: number[];
    showDensity?: boolean;
    densityBandwidth?: number;
    orientation: 'vertical' | 'horizontal';
    forcedDataRange: [number, number];
    forcedMaxFrequency: number;
    showAnnotations?: {
      mean?: boolean;
      median?: boolean;
    };
  }>;
}

/**
 * Facetedモード用のヒストグラム表示コンポーネント
 * 各グループを独立したヒストグラムとして並べて表示
 */
export const HistogramFaceted: React.FC<HistogramFacetedProps> = ({
  groupedData,
  bins,
  width,
  height,
  orientation,
  selectedIndices,
  onBinClick,
  showDensity,
  densityBandwidth,
  showAnnotations,
  HistogramComponent
}) => {
  // 全グループで軸範囲を統一するため、全データの範囲を計算
  const allValues = groupedData.flatMap(g => g.values);
  const globalDataRange = d3.extent(allValues) as [number, number];

  // 全グループで度数軸を統一するため、全グループの最大度数を計算
  const globalMaxFrequency = Math.max(...groupedData.map(group => {
    const min = globalDataRange[0];
    const max = globalDataRange[1];
    const range = max - min;
    const epsilon = range * 0.0001;
    const adjustedMax = max + epsilon;
    const binWidth = (adjustedMax - min) / bins;
    const thresholds: number[] = [];
    for (let i = 0; i <= bins; i++) {
      thresholds.push(min + i * binWidth);
    }
    const histogram = d3.histogram()
      .value(d => d)
      .domain([min, adjustedMax])
      .thresholds(thresholds);
    const histData = histogram(group.values);
    return d3.max(histData, d => d.length) || 0;
  }));

  // Facetedモード用のコンパクトサイズ
  const facetWidth = orientation === 'horizontal' ? 200 : width;
  const facetHeight = orientation === 'vertical' ? 140 : height;

  return (
    <div className={`${styles.facetedContainer} ${
      orientation === 'vertical'
        ? styles.facetedContainerVertical
        : styles.facetedContainerHorizontal
    }`}>
      {groupedData.map((group) => (
        <div key={group.groupName} className={`${styles.facetItem} ${
          orientation === 'horizontal'
            ? styles.facetItemHorizontal
            : styles.facetItemVertical
        }`}>
          <div className={styles.facetTitle}>{group.groupName}</div>
          <HistogramComponent
            data={group.values}
            originalData={group.originalData}
            width={facetWidth}
            height={facetHeight}
            bins={bins}
            onBinClick={onBinClick}
            selectedIndices={selectedIndices}
            showDensity={showDensity}
            densityBandwidth={densityBandwidth}
            orientation={orientation}
            forcedDataRange={globalDataRange}
            forcedMaxFrequency={globalMaxFrequency}
            showAnnotations={showAnnotations}
          />
        </div>
      ))}
    </div>
  );
};
